/**
 * Artifact Capability Predicates
 *
 * Use these instead of checking artifactType === 'note' || artifactType === 'attachment'.
 * Centralizes artifact type logic so adding new types (protocol, etc.) doesn't require
 * updating every component.
 */

import {
  artifactGeneratesEmbeddings,
  artifactHasDetailPage,
  getArtifactDetailRoute,
  isFileArtifact,
  isRichTextArtifact,
} from '@bower/shared/constants/artifact';

/**
 * Whether this type should be treated as an attachment-like artifact.
 * Handles the legacy 'file' type that may still exist in some data.
 */
export function isAttachmentLike(type: string): boolean {
  return type === 'attachment' || type === 'file';
}

/**
 * Get the detail route for an artifact, or null if the type has no detail page.
 * Example: getArtifactRoute('note', '123') => '/notes/123'
 * Example: getArtifactRoute('attachment', '123') => '/attachments/123'
 * Example: getArtifactRoute('file', '123') => null   (legacy type, no page)
 *
 * Attachments DO have a route — it is where every surface except a note's own
 * attachment bar sends them (see `useOpenAttachment`). `useEntityNavigation`
 * still short-circuits them through `isAttachmentLike` first, so the split-view
 * opt-in stays in one place.
 */
export function getArtifactRoute(artifactType: string, id: string): string | null {
  // Handle legacy 'file' type
  if (artifactType === 'file') return null;
  return getArtifactDetailRoute(artifactType, id);
}

/** An artifact's page as an absolute URL (Copy link, Open in new tab). */
export function getArtifactUrl(artifactType: string, id: string): string {
  return window.location.origin + (getArtifactRoute(artifactType, id) ?? `/artifacts/${id}`);
}

// Re-export shared predicates for convenient frontend access
export { artifactGeneratesEmbeddings, artifactHasDetailPage, isFileArtifact, isRichTextArtifact };
